import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p15mjApi, PendingItem, PendingResponse, ApproveResponse, ApprovalFilter } from '../../../services/p15mjApi';

export interface UseP15MJApprovalReturn {
  // State
  loading: boolean;
  approving: boolean;
  pendingItems: PendingItem[];
  total: number;
  selectedKeys: string[];
  filter: ApprovalFilter;

  // Actions
  setFilter: (f: ApprovalFilter) => void;
  setSelectedKeys: (keys: string[]) => void;
  fetchPending: () => Promise<void>;
  batchApprove: () => Promise<ApproveResponse | null>;
}

export function useP15MJApproval(): UseP15MJApprovalReturn {
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pendingItems, setPendingItems] = useState<PendingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [filter, setFilter] = useState<ApprovalFilter>({ codeStart: '', codeEnd: '' });

  const fetchPending = useCallback(async () => {
    if (!filter.codeStart) {
      message.warning('請輸入評鑑代碼起始號碼');
      return;
    }
    setLoading(true);
    setSelectedKeys([]);
    try {
      const resp: PendingResponse = await p15mjApi.getPending(filter);
      setPendingItems(resp.items || []);
      setTotal(resp.total || 0);
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '查詢失敗');
      message.error(msg);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const batchApprove = useCallback(async (): Promise<ApproveResponse | null> => {
    if (selectedKeys.length === 0) {
      message.warning('請至少勾選一筆評鑑進行核准');
      return null;
    }
    setApproving(true);
    try {
      const resp = await p15mjApi.approve(selectedKeys);
      message.success(resp.message);
      // Refresh list after approval
      await fetchPending();
      return resp;
    } catch (err: unknown) {
      const msg = (err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗');
      message.error(msg);
      return null;
    } finally {
      setApproving(false);
    }
  }, [selectedKeys, fetchPending]);

  return {
    loading,
    approving,
    pendingItems,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchPending,
    batchApprove,
  };
}
